// NotificationContext.js
import React, { createContext, useState, useContext, useRef, useEffect, useCallback } from "react";
import notificationBell from "../assets/notification.wav";
import { X, Bell } from "lucide-react";

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

// Exported so Header.jsx can reuse the same helpers
export const avatarColor = (name = "") => {
  const colors = [
    "bg-purple-500", "bg-blue-500", "bg-green-500", "bg-rose-500",
    "bg-amber-500", "bg-teal-500", "bg-indigo-500", "bg-pink-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

// Strips "senderName : " prefix from body so it isn't shown twice
export const parseBody = (body = "", senderName = "") => {
  if (senderName) {
    const prefix = `${senderName} : `;
    if (body.startsWith(prefix)) return body.slice(prefix.length);
  }
  return body;
};

const ToastItem = ({ toast, onClose }) => {
  const [visible, setVisible] = useState(false);
  const hasSender = Boolean(toast.senderName);
  const message = hasSender ? parseBody(toast.body, toast.senderName) : toast.body;

  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 10);
    const exitTimer = setTimeout(() => setVisible(false), 3600);
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, []);

  return (
    <div
      className={`flex items-center gap-3 bg-white border border-gray-200 rounded-2xl shadow-2xl px-4 py-3 w-72 transition-all duration-300 ease-out ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
      }`}
    >
      {hasSender ? (
        <div
          className={`flex-shrink-0 w-11 h-11 rounded-full ${avatarColor(toast.senderName)} flex items-center justify-center text-white text-lg font-semibold select-none shadow-sm`}
        >
          {toast.senderName.charAt(0).toUpperCase()}
        </div>
      ) : (
        <div className="flex-shrink-0 w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center shadow-sm">
          <Bell className="w-5 h-5 text-white" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 leading-tight truncate">
          {hasSender ? toast.senderName : (toast.title || "New Notification")}
        </p>
        {message && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
            {message}
          </p>
        )}
      </div>

      <button
        onClick={onClose}
        className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <span
        className="absolute bottom-0 left-0 h-[3px] bg-blue-500 rounded-b-2xl animate-toast-progress"
        style={{ animationDuration: "4s" }}
      />
    </div>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [toasts, setToasts] = useState([]);
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  useEffect(() => {
    audioRef.current = new Audio(notificationBell);
    audioRef.current.preload = "auto";

    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      const audio = audioRef.current;
      if (audio) {
        audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audioUnlockedRef.current = true;
        }).catch(() => {});
      }
    };

    document.addEventListener("click", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }, []);

  const showBrowserNotification = useCallback((title, body) => {
    if ("Notification" in window && Notification.permission === "granted") {
      const n = new Notification(title, { body, icon: "/logo192.png" });
      n.onclick = () => { window.focus(); n.close(); };
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();

    setNotifications(prev => [...prev, {
      ...notification,
      id,
      timestamp: new Date(),
      type: notification.type || "general",
    }]);

    setToasts(prev => [...prev, { ...notification, id }]);
    setTimeout(() => removeToast(id), 4000);

    playNotificationSound();

    if (document.visibilityState !== "visible") {
      showBrowserNotification(
        notification.title || "New Notification",
        notification.body || ""
      );
    }
  }, [playNotificationSound, showBrowserNotification, removeToast]);

  const clearNotifications = () => setNotifications([]);

  return (
    <NotificationContext.Provider
      value={{ notifications, addNotification, clearNotifications }}
    >
      {children}

      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto relative overflow-hidden">
            <ToastItem toast={toast} onClose={() => removeToast(toast.id)} />
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
